import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// Guardian report palette (Fix 4)
export const RC = {
  volt: '#FFD108',
  success: '#10B981',
  warning: '#E0B800',
  orange: '#FF4F00',
  danger: '#B91C1C',
  neutral: '#B5AFA3',
  ink: '#3D3A35',
  track: '#F2F0EA',
};

export const SEVERITY_COLORS = { critical: RC.danger, high: RC.orange, medium: RC.warning, low: RC.neutral };

// Humanise raw enum category keys (Fix data-Q5)
const CATEGORY_LABELS = {
  crane: 'Cranes', excavator: 'Excavators', excavators: 'Excavators', truck: 'Trucks', trucks: 'Trucks',
  forklift: 'Forklifts', ewp: 'EWP', lifting_gear: 'Lifting gear', support_ute: 'Support utes',
  semi_trailer: 'Semi trailers', cw_semi_trailers: 'Semi trailers',
};
export const humanizeCategory = (c) => {
  if (!c) return '—';
  if (CATEGORY_LABELS[c]) return CATEGORY_LABELS[c];
  return String(c)
    .replace(/_/g, ' ')
    .replace(/\b(\d+)\s?t\b/gi, '$1t')
    .replace(/\ball terrain\b/i, 'all-terrain')
    .replace(/^\w/, (m) => m.toUpperCase());
};

// ── Stat card (Fix 3 + Fix 4): left accent, no icon tile, "—" aware
export const StatCard = ({ title, value, subtitle, accent = RC.volt, testId }) => (
  <div className="bg-white rounded-xl border border-[#E6E2D9] p-4" style={{ borderLeft: `3px solid ${accent}` }} data-testid={testId}>
    <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-[#8A857A]">{title}</p>
    <p className="mt-1.5 font-extrabold text-[30px] leading-none" style={{ fontFamily: 'Manrope, sans-serif', color: value === '—' ? RC.neutral : '#1A1A1A' }}>{value}</p>
    {subtitle && <p className="text-[11.5px] text-[#8F8A7F] mt-1.5">{subtitle}</p>}
  </div>
);

// ── Chart empty state (Fix 1)
export const ChartEmpty = ({ line2, testId }) => (
  <div className="text-center" style={{ padding: '52px 16px' }} data-testid={testId}>
    <p className="text-[13px] font-semibold text-[#1A1A1A]">No data in this period</p>
    {line2 && <p className="text-[12.5px] text-[#8F8A7F] mt-1">{line2}</p>}
  </div>
);

// ── Section card wrapper
export const Panel = ({ title, subtitle, right, children, testId }) => (
  <div className="bg-white rounded-xl border border-[#E6E2D9] overflow-hidden" data-testid={testId}>
    <div className="flex items-center justify-between px-4 pt-4 pb-2">
      <div>
        <span className="text-[15px] font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</span>
        {subtitle && <span className="text-[12.5px] text-[#8F8A7F] ml-2">{subtitle}</span>}
      </div>
      {right}
    </div>
    <div className="px-4 pb-4">{children}</div>
  </div>
);

// ── Grouped column chart for time series (Fix 2)
const ColTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#DAD5CA] rounded-md px-2.5 py-1.5 shadow-sm">
      <p className="text-[11px] font-semibold text-[#1A1A1A] mb-0.5">{label}</p>
      {payload.map((e, i) => (
        <p key={i} className="text-[11px] font-mono" style={{ color: e.color }}>{e.name}: {e.value}</p>
      ))}
    </div>
  );
};

export const ColumnChart = ({ data, bars, xKey = 'label', height = 260, emptyLine2, testId }) => {
  const isEmpty = !data || data.length === 0 || data.every(row => bars.every(b => !row[b.key]));
  if (isEmpty) return <ChartEmpty line2={emptyLine2} testId={testId && `${testId}-empty`} />;
  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} barGap={3} barCategoryGap="22%" margin={{ top: 6, right: 4, left: -18, bottom: 0 }}>
          <XAxis dataKey={xKey} axisLine={{ stroke: '#DAD5CA' }} tickLine={false} tick={{ fontSize: 9.5, fill: '#8F8A7F' }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#B5AFA3', fontFamily: 'JetBrains Mono, monospace' }} width={40} />
          <Tooltip cursor={{ fill: 'rgba(0,0,0,0.03)' }} content={<ColTooltip />} />
          {bars.map(b => (
            <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color} radius={[3, 3, 0, 0]} maxBarSize={16} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-4 mt-2 px-1">
        {bars.map(b => (
          <span key={b.key} className="inline-flex items-center gap-1.5 text-[11.5px] text-[#3D3A35]">
            <span className="w-[9px] h-[9px] rounded-[2px]" style={{ background: b.color }} /> {b.name}
          </span>
        ))}
      </div>
    </div>
  );
};

// ── Horizontal ranking bars with printed value (Fix 2)
export const RankBars = ({ rows, emptyLine2, testId, unit = '', labelWidth = 132 }) => {
  if (!rows || rows.length === 0) return <ChartEmpty line2={emptyLine2} testId={testId && `${testId}-empty`} />;
  const max = Math.max(1, ...rows.map(r => (r.segments ? r.segments.reduce((s, x) => s + x.value, 0) : r.value)));
  return (
    <div className="space-y-[9px]" data-testid={testId}>
      {rows.map((r, i) => {
        const total = r.segments ? r.segments.reduce((s, x) => s + x.value, 0) : r.value;
        return (
          <div key={i} className="grid items-center gap-[11px]" style={{ gridTemplateColumns: `${labelWidth}px 1fr 56px` }}>
            <span className="text-[12.5px] text-[#3D3A35] truncate" title={r.label}>{r.label}</span>
            <div className="h-4 rounded-[4px] overflow-hidden flex" style={{ background: RC.track }}>
              {(r.segments || [{ value: r.value, color: r.color || RC.volt }]).map((seg, j) => (
                <div key={j} style={{ width: `${(seg.value / max) * 100}%`, background: seg.color }} />
              ))}
            </div>
            <span className="text-[11.5px] font-mono text-[#3D3A35] text-right">{typeof r.display === 'string' ? r.display : `${total}${unit}`}</span>
          </div>
        );
      })}
    </div>
  );
};

// ── Single stacked bar + numeric legend for parts-of-a-whole (Fix 2)
export const StackedBar = ({ segments, testId }) => {
  const total = Math.max(1, segments.reduce((s, x) => s + x.value, 0));
  return (
    <div data-testid={testId}>
      <div className="h-[26px] rounded-[6px] overflow-hidden flex" style={{ background: RC.track }}>
        {segments.filter(s => s.value > 0).map((s, i) => (
          <div key={i} style={{ width: `${(s.value / total) * 100}%`, background: s.color }} title={`${s.label}: ${s.value}`} />
        ))}
      </div>
      <div className="grid gap-2.5 mt-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
        {segments.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-[9px] h-[9px] rounded-[2px] shrink-0" style={{ background: s.color }} />
            <span className="font-mono text-[15px] font-bold text-[#1A1A1A]">{s.value}</span>
            <span className="text-[11.5px] text-[#6B6660]">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── overdue-days pill tone
export const overduePill = (days) => days >= 60
  ? { color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA' }
  : { color: '#8A6D00', bg: '#FFF8D6', border: '#EFE0A0' };
